import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { NativeSelect, StatusBadge } from "@/components/common";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export function ClassificationPicker({ account, onSaved }) {
  const [types, setTypes] = useState([]);
  const [sel, setSel] = useState(account.consumer_type_id || "");
  useEffect(() => { api.get("/admin/system-options/consumer-types").then((r) => setTypes(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put(`/admin/accounts/${account.id}/classification`, { consumer_type_id: sel }); toast.success("Classification saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="classification-picker">
      <span className="text-slate-500">Consumer type (used for rate computation):</span>
      <NativeSelect value={sel} onChange={setSel} placeholder="Not assigned" className="w-64" testId="classification-select"
        options={types.map((t) => ({ value: t.id, label: `${t.category_name} · ${t.name}` }))} />
      <Button size="sm" variant="outline" disabled={!sel || sel === account.consumer_type_id} onClick={save} data-testid="classification-save">Save</Button>
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function StatementViewer({ accountId, bill, onClose }) {
  const { can } = useAuth();
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  const [url, setUrl] = useState(null);
  useEffect(() => {
    api.get(`/admin/accounts/${accountId}/statements`).then((r) => {
      setList(r.data);
      setSel(r.data.find((s) => s.bill_id === bill.id)?.id || r.data[0]?.id || null);
    }).catch((e) => toast.error(errMsg(e)));
  }, [accountId, bill.id]);
  useEffect(() => {
    let u;
    if (sel) api.get(`/admin/statements/${sel}/pdf`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch((e) => toast.error(errMsg(e)));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [sel]);
  const st = list?.find((s) => s.id === sel);
  const print = () => { const w = window.open(url); w?.addEventListener("load", () => w.print()); };
  const resend = async () => { try { const r = await api.post(`/admin/statements/${sel}/resend`); toast.success(`Email: ${r.data.label || r.data.status}`); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="statement-viewer">
        <DialogHeader><DialogTitle>Billing statement</DialogTitle></DialogHeader>
        {list === null ? <p className="text-sm text-slate-500">Loading…</p> : !st ? <p className="text-sm text-slate-600 py-8 text-center" data-testid="no-statement">No Billing Statement Available</p> : <>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <NativeSelect value={sel} onChange={setSel} testId="statement-period" className="w-48" options={list.map((s) => ({ value: s.id, label: `${MONTHS[s.billing_month - 1]} ${s.billing_year}` }))} />
            <span className="font-mono" data-testid="statement-no">{st.statement_no}</span><span className="text-slate-500">generated {fmtDate(st.created_at)}</span>
            <StatusBadge status={st.status} /><span className="text-xs text-slate-500" data-testid="statement-email-status">Email: {st.email?.label || st.email?.status}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={!url} onClick={() => window.open(url, "_blank", "noopener")} data-testid="statement-open">Open in new tab</Button>
            <Button size="sm" variant="outline" disabled={!url} asChild={!!url}><a href={url || "#"} download={`${st.statement_no}.pdf`} data-testid="statement-download">Download PDF</a></Button>
            <Button size="sm" variant="outline" disabled={!url} onClick={print} data-testid="statement-print">Print</Button>
            {can("billing.upload") && <Button size="sm" variant="outline" onClick={resend} data-testid="statement-resend">Resend email</Button>}
          </div>
          {url ? <iframe title="Billing statement" src={url} className="w-full h-[65vh] border rounded" data-testid="statement-pdf" /> : <p className="text-sm text-slate-500">Loading PDF…</p>}
        </>}
      </DialogContent>
    </Dialog>
  );
}
